import { useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Expander from 'components/utils/Expander'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'

export interface OfferPropertySurroundingsDisplayPlace {
  id: string
  name: string
  distanceLabel: string
}

interface Props {
  title: string
  icon: ReactNode
  places: Array<OfferPropertySurroundingsDisplayPlace>
}

const MOBILE_VISIBLE_PLACE_COUNT = 2

const PlaceRow = styled(Group)`
  padding: ${rem(8)} 0;
`

const PlaceDistance = styled(BodyText)`
  flex-shrink: 0;
  white-space: nowrap;
`

function PlaceList({ places }: { places: Array<OfferPropertySurroundingsDisplayPlace> }) {
  return <Group direction="vertical">
    {places.map((place) => (
      <PlaceRow direction="horizontal" horizontalAlign="space-between" gap={16} key={place.id}>
        <BodyText variant="small">{place.name}</BodyText>
        <PlaceDistance variant="small" colour="neutral-three">{place.distanceLabel}</PlaceDistance>
      </PlaceRow>
    ))}
  </Group>
}

function OfferPropertySurroundingsCategory({ title, icon, places }: Props) {
  const intl = useIntl()
  const [expanded, toggleExpanded] = useToggle(false)

  const hasMobileHiddenPlaces = places.length > MOBILE_VISIBLE_PLACE_COUNT
  const mobileVisiblePlaces = useMemo(() => places.slice(0, MOBILE_VISIBLE_PLACE_COUNT), [places])
  const mobileHiddenPlaces = useMemo(() => places.slice(MOBILE_VISIBLE_PLACE_COUNT), [places])

  const readMoreLabel = intl.formatMessage({
    id: 'Kaj2PiSeMs7Hd8C59nV4',
    defaultMessage: 'Read more',
  })
  const readLessLabel = intl.formatMessage({
    id: 't5nVBe_AISgAOJTeTir6',
    defaultMessage: 'Read less',
  })

  return (
    <Group direction="vertical">
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {icon}
        <BodyText variant="medium" weight="bold">{title}</BodyText>
      </Group>
      <CSSBreakpoint min="tablet">
        <PlaceList places={places} />
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <PlaceList places={mobileVisiblePlaces} />
        {hasMobileHiddenPlaces && <>
          <Expander expanded={expanded}>
            <PlaceList places={mobileHiddenPlaces} />
          </Expander>
          <TextLink
            endIcon={<DropdownChevron direction={expanded ? 'up' : 'down'} />}
            onClick={toggleExpanded}
            size="small"
            weight="regular"
          >
            {expanded ? readLessLabel : readMoreLabel}
          </TextLink>
        </>}
      </CSSBreakpoint>
    </Group>
  )
}

export default OfferPropertySurroundingsCategory
